import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { get } from 'lodash'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import {
  TruckIcon,
  CheckCircleIcon,
  CalendarIcon,
  ExclamationCircleIcon,
  RefreshIcon,
  ClipboardListIcon,
  ClockIcon,
  BadgeCheckIcon,
  BanIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/outline'
import apiGet from 'lib/network/apiGet'
import { isPartnerDashboardUser, usesTenantDashboardUi } from 'lib/auth/dashboardRole'

const count = (obj, key) => (obj && obj[key] != null ? Number(obj[key]) : 0)

const StatTile = ({ icon: Icon, label, value, loading, accent }) => (
  <div
    className={classNames(
      'group relative flex flex-col overflow-hidden rounded-xl border bg-white p-3 shadow-sm transition-all duration-200 hover:border-slate-300/90 hover:shadow-md sm:p-4',
      accent.border
    )}
  >
    <div className="mb-3 flex items-start justify-between gap-2">
      <div className={classNames('inline-flex rounded-lg p-2 shadow-inner', accent.iconWrap)}>
        <Icon className={classNames('h-5 w-5 shrink-0', accent.iconColor)} strokeWidth={1.75} aria-hidden />
      </div>
      <div className="flex min-h-[2rem] min-w-[2.5rem] items-center justify-end">
        {loading ? (
          <span className="inline-block h-8 w-11 animate-pulse rounded-md bg-slate-200" aria-hidden />
        ) : (
          <span className="text-right text-2xl font-extrabold tabular-nums leading-none tracking-tight text-slate-900 sm:text-[1.65rem]">{value}</span>
        )}
      </div>
    </div>
    <p className="text-[10px] font-bold uppercase leading-tight tracking-wider text-slate-500">{label}</p>
  </div>
)

const SectionShell = ({ variantIcon: VariantIcon, title, subtitle, children, showLiveConnected, liveConnectedLabel }) => (
  <div className="mt-5 mb-6 px-4 sm:mt-6 sm:px-5">
    <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-md ring-1 ring-slate-900/[0.06]">
      <div className="relative border-b border-slate-200/70 bg-gradient-to-r from-[#091c2d] via-[#142a3f] to-[#1e3a52] px-4 py-3.5 sm:px-5 sm:py-4">
        <div aria-hidden className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full bg-teal-400/15 blur-2xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-6 left-1/3 h-20 w-20 rounded-full bg-white/5 blur-xl" />
        <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 shadow-inner ring-1 ring-white/20">
              <VariantIcon className="h-6 w-6 text-white" strokeWidth={1.5} aria-hidden />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold tracking-tight text-white sm:text-base">{title}</h3>
              <p className="mt-0.5 text-[11px] font-medium leading-snug text-white sm:text-xs">{subtitle}</p>
            </div>
          </div>
          {showLiveConnected && liveConnectedLabel ? (
            <div className="flex shrink-0 items-center gap-2 self-start rounded-full bg-emerald-500/25 px-3 py-1.5 text-[11px] font-semibold text-white ring-1 ring-emerald-400/50 sm:self-center">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              <span className="leading-tight">{liveConnectedLabel}</span>
            </div>
          ) : null}
        </div>
      </div>
      <div className="bg-gradient-to-b from-slate-50/90 to-white px-3 py-4 sm:px-5 sm:py-5">{children}</div>
    </div>
  </div>
)

/**
 * Company-scoped stats from GET /admin/company-dashboard/stats.
 * @param {'cars' | 'bookings'} variant
 * @param {string|number} [reloadKey] — bump when list data changes to refresh stats
 * @param {boolean} [streamConnected] — bookings only: show live-updates pill in header
 */
const CompanyStatsSummary = ({ variant, reloadKey = 0, streamConnected = false }) => {
  const { t } = useTranslation()
  const userFromStore = useSelector(state => get(state, 'user.user'))
  const companyBranchFilterId = useSelector(state => get(state, 'settings.companyBranchFilterId'))
  const isPartnerRole = isPartnerDashboardUser(userFromStore)
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(false)

  const enabled = usesTenantDashboardUi(userFromStore)
  const statsEndpoint = isPartnerRole ? 'partner-dashboard/stats' : 'company-dashboard/stats'

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    setLoading(true)
    apiGet(statsEndpoint, {}, false)
      .then(res => {
        if (!cancelled && res?.success && res.data) setStats(res.data)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [enabled, reloadKey, companyBranchFilterId, statsEndpoint])

  if (!enabled) return null

  if (variant === 'cars') {
    const c = stats?.cars?.byStatus || {}
    const tiles = [
      {
        key: 'total',
        label: t('companyDashboard.statsDetail.totalCars'),
        value: stats?.cars?.total ?? '—',
        icon: TruckIcon,
        accent: { border: 'border-slate-200/90', iconWrap: 'bg-slate-100 ring-1 ring-slate-200/80', iconColor: 'text-slate-700' },
      },
      {
        key: 'active',
        label: t('cars.status.active'),
        value: count(c, 'active'),
        icon: CheckCircleIcon,
        accent: { border: 'border-emerald-200/80', iconWrap: 'bg-emerald-50 ring-1 ring-emerald-100', iconColor: 'text-emerald-700' },
      },
      {
        key: 'booked',
        label: t('cars.status.booked'),
        value: count(c, 'booked'),
        icon: CalendarIcon,
        accent: { border: 'border-sky-200/80', iconWrap: 'bg-sky-50 ring-1 ring-sky-100', iconColor: 'text-sky-700' },
      },
      {
        key: 'oos',
        label: t('cars.status.outOfService'),
        value: count(c, 'out of service'),
        icon: ExclamationCircleIcon,
        accent: { border: 'border-rose-200/80', iconWrap: 'bg-rose-50 ring-1 ring-rose-100', iconColor: 'text-rose-700' },
      },
      {
        key: 'inProgress',
        label: t('cars.status.inProgress'),
        value: count(c, 'inProgress'),
        icon: RefreshIcon,
        accent: { border: 'border-amber-200/80', iconWrap: 'bg-amber-50 ring-1 ring-amber-100', iconColor: 'text-amber-700' },
      },
    ]

    return (
      <SectionShell variantIcon={TruckIcon} title={t('cars.statsStrip')} subtitle={t('cars.statsStripSubtitle')}>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 lg:gap-4">
          {tiles.map(tile => (
            <StatTile key={tile.key} icon={tile.icon} label={tile.label} value={tile.value} loading={loading} accent={tile.accent} />
          ))}
        </div>
      </SectionShell>
    )
  }

  if (variant === 'bookings') {
    const b = stats?.bookings?.byStatus || {}
    const p = stats?.bookings?.byPaymentStatus || {}
    const rejectedOrCancelled = count(b, 'rejected') + count(b, 'cancelled')
    const tiles = [
      {
        key: 'total',
        label: t('companyDashboard.statsDetail.totalBookings'),
        value: stats?.bookings?.total ?? '—',
        icon: ClipboardListIcon,
        accent: { border: 'border-slate-200/90', iconWrap: 'bg-slate-100 ring-1 ring-slate-200/80', iconColor: 'text-slate-700' },
      },
      {
        key: 'pending',
        label: t('companyDashboard.status.pending'),
        value: count(b, 'pending'),
        icon: ClockIcon,
        accent: { border: 'border-slate-300/90', iconWrap: 'bg-slate-100 ring-1 ring-slate-200', iconColor: 'text-slate-700' },
      },
      {
        key: 'confirmed',
        label: t('companyDashboard.status.confirmed'),
        value: count(b, 'confirmed'),
        icon: CheckCircleIcon,
        accent: { border: 'border-emerald-200/80', iconWrap: 'bg-emerald-50 ring-1 ring-emerald-100', iconColor: 'text-emerald-700' },
      },
      {
        key: 'completed',
        label: t('companyDashboard.status.completed'),
        value: count(b, 'completed'),
        icon: BadgeCheckIcon,
        accent: { border: 'border-teal-200/80', iconWrap: 'bg-teal-50 ring-1 ring-teal-100', iconColor: 'text-teal-700' },
      },
      {
        key: 'rejected',
        label: t('companyDashboard.status.rejected'),
        value: rejectedOrCancelled,
        icon: BanIcon,
        accent: { border: 'border-rose-200/80', iconWrap: 'bg-rose-50 ring-1 ring-rose-100', iconColor: 'text-rose-700' },
      },
      {
        key: 'paid',
        label: t('bookings.paymentStatus.paid'),
        value: count(p, 'paid'),
        icon: CurrencyDollarIcon,
        accent: { border: 'border-green-200/80', iconWrap: 'bg-green-50 ring-1 ring-green-100', iconColor: 'text-green-700' },
      },
    ]

    return (
      <SectionShell
        variantIcon={ClipboardListIcon}
        title={t('bookings.statsStrip')}
        subtitle={t('bookings.statsStripSubtitle')}
        showLiveConnected={Boolean(streamConnected)}
        liveConnectedLabel={t('booking.messages.liveUpdatesConnected')}
      >
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6 xl:gap-4">
          {tiles.map(tile => (
            <StatTile key={tile.key} icon={tile.icon} label={tile.label} value={tile.value} loading={loading} accent={tile.accent} />
          ))}
        </div>
      </SectionShell>
    )
  }

  return null
}

export default CompanyStatsSummary
